import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { permissionDef, tokenPresetPermissions, type Permission, type TokenPreset } from "@metrickle/schema";
import { Logo } from "../components/layout";
import { Button, Callout, Disclosure, Field, FieldError, RadioCards, SelectField, Skeleton } from "../components/ui";
import { useOrgAccess } from "../lib/access";
import { api, ApiError } from "../lib/api";
import { ACCESS_PRESETS, redirectLabel } from "../lib/assistant-access";
import { authClient } from "../lib/auth";
import { track } from "../lib/track";
import { usePageTitle } from "../lib/page-title";

interface AuthorizationRequest {
  id: string;
  client: { name: string; verifiedHost: string | null; uri: string | null };
  redirect: { kind: "web" | "loopback" | "app"; host: string };
  loopbackOnly: boolean;
  asksToDraft: boolean;
  expiresAt: number;
}

/** Reached from /oauth/authorize: someone's assistant asks to connect, and they decide. */
export function ConnectAuthorize() {
  usePageTitle("Connect an assistant");
  const [params] = useSearchParams();
  const requestId = params.get("request") ?? "";
  const request = useQuery({
    queryKey: ["oauth-request", requestId],
    queryFn: () => api<AuthorizationRequest>(`/oauth/requests/${encodeURIComponent(requestId)}`),
    enabled: !!requestId,
    retry: false,
  });
  // A consent page inside someone else's frame could be clicked through without the person knowing.
  const framed = window.top !== window.self;

  return (
    <div className="flex min-h-dvh items-start justify-center px-4 py-12 sm:items-center">
      <main className="w-full max-w-lg">
        <Logo className="mb-8 text-lg" />
        {framed ? (
          <Notice title="Open this page in its own tab">For your security, Metrickle doesn't let other sites show this page inside theirs.</Notice>
        ) : !requestId || request.isError ? (
          <Notice title="This connection request has expired">
            {request.error instanceof ApiError && request.error.status !== 404
              ? "Couldn't load it. Reload to try again."
              : "Requests last 15 minutes and work once. Go back to the app you were connecting and start again."}
          </Notice>
        ) : request.data ? (
          <Consent request={request.data} />
        ) : (
          <div role="status" aria-busy="true">
            <Skeleton className="h-72" />
            <span className="sr-only">Loading…</span>
          </div>
        )}
      </main>
    </div>
  );
}

function Notice({ title, children }: { title: string; children: string }) {
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
      <p className="mt-2 text-sm text-ink-2">{children}</p>
    </>
  );
}

function Consent({ request }: { request: AuthorizationRequest }) {
  const session = authClient.useSession();
  const orgs = authClient.useListOrganizations();
  const active = authClient.useActiveOrganization();
  const [pickedOrg, setPickedOrg] = useState<string>();
  const orgId = pickedOrg ?? active.data?.id ?? orgs.data?.[0]?.id;
  const access = useOrgAccess(orgId);
  const held = new Set(access.data?.permissions ?? []);
  // Always starts on read only; drafting is the person's choice, not the app's.
  const [preset, setPreset] = useState<TokenPreset>("read");
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState<string | null>(null);
  const leavingRef = useRef<HTMLParagraphElement>(null);
  const errorId = useId();
  const verifiedId = useId();

  const permissions: Permission[] = tokenPresetPermissions(preset, held);
  const { client, redirect } = request;
  const back = redirectLabel(redirect);

  useEffect(() => {
    if (leaving) leavingRef.current?.focus();
  }, [leaving]);

  const decide = async (decision: "approve" | "deny") => {
    setError(null);
    if (decision === "approve" && !permissions.length) {
      setError("You don't have any of this access in this workspace. Choose another workspace.");
      return;
    }
    setBusy(decision);
    try {
      const { redirectTo } = await api<{ redirectTo: string }>(`/oauth/requests/${encodeURIComponent(request.id)}/${decision}`, {
        method: "POST",
        json: decision === "approve" ? { organizationId: orgId, preset } : {},
      });
      track(decision === "approve" ? "ai_connector_approved" : "ai_connector_denied", decision === "approve" ? { preset, permissions: permissions.length } : undefined);
      setLeaving(redirectTo);
      window.location.assign(redirectTo);
    } catch (err) {
      setBusy(null);
      setError(
        err instanceof ApiError && err.status === 404
          ? "This request has expired. Go back to the app you were connecting and start again."
          : err instanceof Error
            ? err.message
            : "Something went wrong. Try again.",
      );
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void decide("approve");
  };

  if (leaving) {
    return (
      <>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Returning you to {client.name}</h1>
        <p ref={leavingRef} tabIndex={-1} role="status" className="mt-2 text-sm text-ink-2 outline-none">
          You can close this tab if {client.name} has opened.{" "}
          <a className="text-accent-text underline" href={leaving}>
            Continue to {back}
          </a>
        </p>
      </>
    );
  }

  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Connect {client.name} to Metrickle?</h1>
      <p className="mt-1 text-sm text-ink-2">
        {client.name} will be able to use one workspace as you{session.data ? ` (${session.data.user.email})` : ""}, with the access you choose here. You can disconnect it at any time
        under Connect AI.
      </p>

      <div className="mt-5 space-y-2 rounded-xl border border-line bg-surface p-4 text-sm">
        <p id={verifiedId} className="flex gap-2 text-ink-2">
          {client.verifiedHost ? (
            <>
              <ShieldCheck size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-good" />
              <span>
                Identified by <strong className="font-medium text-ink">{client.verifiedHost}</strong>, which publishes this app's details.
              </span>
            </>
          ) : (
            <>
              <ShieldAlert size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-ink-3" />
              <span>Metrickle hasn't verified this name: it's what the app calls itself. Only continue if you started connecting it just now.</span>
            </>
          )}
        </p>
        <p className="text-ink-2">
          After you decide, you'll go back to <strong className="font-medium text-ink">{back}</strong>.
        </p>
        {request.loopbackOnly && (
          <Callout tone="warn" title="Check this is you.">
            Any program on this computer can use this address. Only approve if you just started connecting from an app on this device, such as Claude Code.
          </Callout>
        )}
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4" aria-describedby={verifiedId}>
        <Field label="Workspace" hint={`${client.name} works only here.`}>
          <SelectField
            className="w-full"
            items={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
            value={orgId}
            onChange={(v) => {
              setPickedOrg(v);
              setError(null);
            }}
          />
        </Field>
        <div>
          <RadioCards
            label="Access"
            name="preset"
            items={ACCESS_PRESETS}
            value={preset}
            onValueChange={(v) => {
              setPreset(v as TokenPreset);
              setError(null);
            }}
          />
          {request.asksToDraft && <p className="mt-1.5 text-xs text-ink-3">{client.name} asked to read and draft. Read only is enough to answer questions.</p>}
        </div>
        {access.isError && <FieldError>Couldn't load your access in this workspace. Reload to try again.</FieldError>}
        {access.data && (
          <Disclosure
            summary={
              <>
                What {client.name} can do<span className="sr-only"> ({permissions.length} permissions)</span>
              </>
            }
            contentClassName="text-sm text-ink-2"
          >
            <ul className="list-disc space-y-0.5 pl-5 text-xs">
              {permissions.map((p) => (
                <li key={p}>{permissionDef(p)?.label ?? p}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-ink-3">
              Never more than you can do yourself, even if your role changes later. Protecting tasks, putting surveys live, deleting, settings and the team always need you in
              the dashboard.
            </p>
          </Disclosure>
        )}
        {error && (
          <FieldError id={errorId} role="alert">
            {error}
          </FieldError>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            variant="primary"
            type="submit"
            disabled={!!busy || !access.data}
            pending={busy === "approve"}
            pendingLabel="Connecting…"
            aria-describedby={error ? errorId : undefined}
          >
            Allow {client.name}
          </Button>
          <Button type="button" disabled={!!busy} pending={busy === "deny"} pendingLabel="Cancelling…" onClick={() => void decide("deny")}>
            Deny
          </Button>
        </div>
      </form>
    </>
  );
}
